<template>
  <ModalDialog
    v-model="isShow"
    :targetClass="isEmbed ? 'submit-button' : null"
    :level="10"
    @close="onReject()"
  >
    <div class="p-5" slot="dialog-content">
      <div v-if="innerHtml" v-html="formatContent(innerHtml, ...params)"></div>
      <h2 v-else>{{ formatContent(message, ...params) }}</h2>
      <div v-if="showReasonInput">
        <label>{{ reasonOnConfirmLabel }}</label>
        <textarea
          rows="3"
          class="form-control"
          v-model="confirmReason"
        ></textarea>
        <p v-if="isRequired" class="required-message">
          *Please fill out a reason
        </p>
      </div>
      <hr />
      <div class="button-container">
        <div class="reject-btn-container">
          <button class="mylisting-button secondary-button" @click="onReject()">
            {{ rejectLabel }}
          </button>
        </div>
        <div class="spacing"></div>
        <div class="accept-btn-container">
          <button
            v-if="acceptLabel2"
            class="mylisting-button dark-button"
            @click="onAccept2()"
          >
            {{ acceptLabel2 }}
          </button>
          <button class="mylisting-button danger-button" @click="onAccept()">
            {{ acceptLabel }}
          </button>
        </div>
      </div>
    </div>
  </ModalDialog>
</template>
<script>
/* This component is used to show confirm dialog.
To show an confirm, call this.showConfirmDialog(confirmObject)
Structure of confirmObject:
{
    message: 'String',
    showReasonInput: 'Boolean', <- This field is optional, if not set, use default: false, on True shows input textbox for reason of confirmation
    reasonOnConfirmLabel: 'String', This field is optional, if not set and showReasonInput is True, use default: Reason for Confirmation
    acceptLabel: 'String', <- This field is optional, if not set, use default: Yes
    rejectLabel: 'String',  <- This field is optional, if not set, use default: No
    onAcceptCallback: function reference <- This field is optional, if set, the function will be called after click accept button 
    onAcceptCallbackData: object <- This field is optional, it is parameter of onAcceptCallback function
}

*/
import ModalDialog from "./ModalDialog";

export default {
  name: "ConfirmDialog",
  data: function () {
    return {
      message: "",
      acceptLabel: "",
      acceptLabel2: "",
      rejectLabel: "",
      confirmReason: "",
      reasonOnConfirmLabel: "",
      requiredConfirmReason: true, // Default: required
      isRequired: false,
      showReasonInput: false,
      onAcceptCallback: null,
      onAcceptCallbackData: null,
      onAcceptCallback2: null,
      onAcceptCallbackData2: null,
      onRejectCallback: null,
      onRejectCallbackData: null,
      params: [],
      innerHtml: null,
      isShow: false,
      isAcceptClick: false,
    };
  },
  components: {
    ModalDialog,
  },
  computed: {
    /* Check if the app is displayed inside an iframe or embed tag
    https://stackoverflow.com/questions/326069/how-to-identify-if-a-webpage-is-being-loaded-inside-an-iframe-or-directly-into-t
    */
    isEmbed() {
      try {
        return window.self !== window.top;
      } catch (e) {
        return true;
      }
    },
  },
  methods: {
    onReject() {
      this.isShow = false;
      if (!this.isAcceptClick) {
        if (this.onRejectCallback) {
          if (this.onRejectCallbackData) {
            this.onRejectCallback(this.onRejectCallbackData);
          } else {
            this.onRejectCallback();
          }
        }
      }
      this.showConfirmDialog(null);
    },
    onAccept() {
      this.isAcceptClick = true;
      let required = false;
      if (this.showReasonInput) {
        if (this.requiredConfirmReason && !this.confirmReason) {
          this.isRequired = true;
          required = true;
        } else {
          this.onAcceptCallbackData = this.confirmReason;
        }
      }

      if (!required) {
        this.isRequired = false;
        this.isShow = false;

        if (this.onAcceptCallback) {
          if (!this.onAcceptCallbackData) {
            this.onAcceptCallback();
          } else {
            this.onAcceptCallback(this.onAcceptCallbackData);
          }
        }
        this.showConfirmDialog(null);
      }
    },
    onAccept2() {
      this.isAcceptClick = true;
      this.showConfirmDialog(null);
      if (this.onAcceptCallback2) {
        if (!this.onAcceptCallbackData2) {
          if (this.confirmReason) {
            this.onAcceptCallback2(this.confirmReason);
          } else {
            this.onAcceptCallback2();
          }
        } else {
          this.onAcceptCallback2(this.onAcceptCallbackData2);
        }
      }
    },
  },
  created() {
    this.$store.watch(
      (state) => state.dialogs.confirmDialog,
      (value) => {
        if (!value) {
          this.isShow = false;
          this.showConfirmDialog(null);
        } else {
          console.log(value);
          if (this.isShow) {
            return;
          }
          this.isAcceptClick = false;
          this.message = value.message;
          if (!value.acceptLabel) {
            this.acceptLabel = "Yes";
          } else {
            this.acceptLabel = value.acceptLabel;
          }
          if (value.acceptLabel2) {
            this.acceptLabel2 = value.acceptLabel2;
          } else {
            this.acceptLabel2 = "";
          }
          if (!value.rejectLabel) {
            this.rejectLabel = "No";
          } else {
            this.rejectLabel = value.rejectLabel;
          }
          this.showReasonInput = false;
          if (value.showReasonInput) {
            this.requiredConfirmReason =
              value.requiredConfirmReason === false ? false : true;
            this.showReasonInput = true;
            if (!value.reasonOnConfirmLabel) {
              this.reasonOnConfirmLabel = "Reason for Confirmation";
            } else {
              this.reasonOnConfirmLabel = value.reasonOnConfirmLabel;
            }
            this.confirmReason = "";
          }
          this.innerHtml = value.innerHtml;
          this.onAcceptCallback = value.onAcceptCallback;
          this.onAcceptCallbackData = value.onAcceptCallbackData;
          this.onAcceptCallback2 = value.onAcceptCallback2;
          this.onAcceptCallbackData2 = value.onAcceptCallbackData2;
          this.onRejectCallback = value.onRejectCallback;
          this.onRejectCallbackData = value.onRejectCallbackData;
          this.params = value.params ? value.params : [];
          const key = value.key;
          let isMultiLanguageAcceptLabel = false;
          let isMultiLanguageRejectLabel = false;
          let isMultiLanguageAcceptLabel2 = false;
          if (key) {
            const message = this.getLabelByKeys(
              `CONFIRM_DIALOGS.${key}.MESSAGE`
            );
            if (message) {
              this.message = message;
            }
            const innerHtml = this.getLabelByKeys(
              `CONFIRM_DIALOGS.${key}.INNER_HTML`
            );
            if (innerHtml) {
              this.innerHtml = innerHtml;
            }
            const acceptLabel = this.getLabelByKeys(
              `CONFIRM_DIALOGS.${key}.ACCEPT_LABEL`
            );
            if (acceptLabel) {
              this.acceptLabel = acceptLabel;
              isMultiLanguageAcceptLabel = true;
            }
            const acceptLabel2 = this.getLabelByKeys(
              `CONFIRM_DIALOGS.${key}.ACCEPT_LABEL2`
            );
            if (acceptLabel2) {
              this.acceptLabel2 = acceptLabel2;
              isMultiLanguageAcceptLabel2 = true;
            }
            const rejectLabel = this.getLabelByKeys(
              `CONFIRM_DIALOGS.${key}.REJECT_LABEL`
            );
            if (rejectLabel) {
              this.rejectLabel = rejectLabel;
              isMultiLanguageRejectLabel = true;
            }
          }
          // Use multi-language for accept label
          if (!isMultiLanguageAcceptLabel) {
            const displayAcceptLabel = this.getLabelByKeys(
              `BUTTONS.${this.acceptLabel.toUpperCase()}`
            );
            if (displayAcceptLabel) {
              this.acceptLabel = displayAcceptLabel;
            }
          }
          // Use multi-language for accept label 2
          if (!isMultiLanguageAcceptLabel2) {
            const displayAcceptLabel2 = this.getLabelByKeys(
              `BUTTONS.${this.acceptLabel2.toUpperCase()}`
            );
            if (displayAcceptLabel2) {
              this.acceptLabel2 = displayAcceptLabel2;
            }
          }
          // Use multi-language for reject label
          if (!isMultiLanguageRejectLabel) {
            const displayRejectLabel = this.getLabelByKeys(
              `BUTTONS.${this.rejectLabel.toUpperCase()}`
            );
            if (displayRejectLabel) {
              this.rejectLabel = displayRejectLabel;
            }
          }
          this.isShow = true;
        }
      },
      { immediate: true }
    );
  },
};
</script>

<style scoped>
@media (max-width: 768px) {
  h2 {
    font-size: 24px;
  }

  h4 {
    font-size: 20px;
  }
}

.button-container {
  display: flex;
  flex-wrap: wrap;
}

.spacing {
  flex-grow: 1;
}

.accept-btn-container,
.reject-btn-container {
  text-align: center;
}

@media (max-width: 400px) {
  .button-container {
    display: block;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/common/global-dialogs/ConfirmDialog.vue